import {z} from 'zod';
import {objectIdString, optionalNonNull} from '@packages/utilities/zod';

/**
 * Maximum lengths for various page fields.
 * To make the Page Zod type fully aligned with the Mongoose PageSchema,
 * those values should be referenced as max length values for the corresponding
 * page fields. We're not applying them directly right now because we don't want
 * to introduce a new failure mechanism. Over time though they should be applied
 * for better validation.
 * TODO: apply max lengths to Page Zod type fields
 */
/** Shared constants & helpers for the 50 000-char custom-code limit UI. */
export const CUSTOM_CODE_CHAR_LIMIT = 50000;
export const CUSTOM_CODE_WARNING_THRESHOLD = CUSTOM_CODE_CHAR_LIMIT - 100;

/** Red "over limit" label.  Count is capped at limit+100 (shown as "50,100+"). */
export function formatOverLimitLabel(charCount: number, limit: number): string {
  const cap = limit + 100;
  const display =
    charCount >= cap ? `${cap.toLocaleString()}+` : charCount.toLocaleString();
  return `${display}/${limit.toLocaleString()} — Over character limit.`;
}

/** Gray "near limit" label. */
export function formatNearLimitLabel(charCount: number, limit: number): string {
  return `${charCount.toLocaleString()} / ${limit.toLocaleString()} characters used.`;
}

// `title` and `slug` are also consumed by `Page._importSnapshotV1`'s
// `util.truncateFields` call, so a snapshot/site restore silently truncates
// an over-limit title/slug (running after the unique-slug `-<guid>` suffix
// is appended, so truncation can also eat part of that suffix). This is
// intentional here — it mirrors the new save-time guard instead of failing
// the restore outright.
export const PAGE_FIELD_MAX_LENGTHS = {
  title: 256,
  slug: 256,
  seoTitle: 2000,
  seoDesc: 2000,
  ogTitle: 2000,
  ogDesc: 2000,
  rssTitle: 2000,
  rssDesc: 2000,
  rssLink: 2000,
  rssItemTitle: 2000,
  rssItemDesc: 2000,
  head: 50000,
  postBody: 50000,
  searchTitle: 2000,
  searchDescription: 2000,
  searchSnippetOverride: 2000,
  jsonLdSchema: 50000,
} as const;

export const TemplatePages = z.enum([
  'login',
  'signup',
  'resetPassword',
  'updatePassword',
  'accessDenied',
  'userAccount',
]);
export type TemplatePages = z.infer<typeof TemplatePages>;

export const PAGE_UTIL_KEYS = [
  'usys-log-in',
  'usys-sign-up',
  'usys-reset-password',
  'usys-update-password',
  'usys-access-denied',
  'usys-user-account',
] as const;
export const PageUtilKeys = z.enum(PAGE_UTIL_KEYS);
export type PageUtilKeys = z.infer<typeof PageUtilKeys>;

export const AVAILABLE_UTILITY_PAGE_KEYS = [
  '401',
  '404',
  'search',
  'ecommerce-checkout',
  'ecommerce-paypal-checkout',
  'ecommerce-confirmation',
  ...PAGE_UTIL_KEYS,
] as const;
export const AvailableUtilityPages = z.enum(AVAILABLE_UTILITY_PAGE_KEYS);
export type AvailableUtilityPages = z.infer<typeof AvailableUtilityPages>;

/**
 * IMPORTANT: Index pages `slug` should be `null` and detail pages `slug`
 * should start with `detail_`.  Branch pages should start with `branch_` unless
 * they are branches of dynamic pages, in which case they should start with
 * `detail_branch_`.
 */
export const PageSlug = z
  .string()
  .max(PAGE_FIELD_MAX_LENGTHS.slug)
  .regex(/^[a-zA-Z0-9-_]+$/);
export type PageSlug = z.infer<typeof PageSlug>;

export const PageTitle = z
  .string()
  .max(PAGE_FIELD_MAX_LENGTHS.title)
  .refine((value) => value.trim().length > 0);
export type PageTitle = z.infer<typeof PageTitle>;

export const PAGE_TYPES = ['page', 'folder'] as const;
export const PageType = z.enum(PAGE_TYPES).default('page');
export type PageType = z.infer<typeof PageType>;

const CustomCodeOrder = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);

/**
 * Which of a site branch's two page trees a row belongs to.
 * `'working'` — the editable tree the branch edits.
 * `'base'` — the frozen fork-time twin that diffs and merge run against.
 * Unset on main pages and legacy page-branch rows.
 */
export const pageBranchRoles = ['working', 'base'] as const;
export const PageBranchRole = z.enum(pageBranchRoles);
export type PageBranchRole = z.infer<typeof PageBranchRole>;

export const PageKind = z.object({
  kind: z.enum(['component']),
  meta: z.object({
    componentId: z.string(),
  }),
});
export type PageKind = z.infer<typeof PageKind>;

export const Page = z
  .object({
    id: objectIdString,

    // Basic page properties
    parent: objectIdString.nullable().default(null),
    sortPos: optionalNonNull(z.number()),
    type: PageType,

    title: z.string(),
    slug: z.string().nullish(),
    utilKey: AvailableUtilityPages.nullable().default(null),
    site: objectIdString,

    // DOM tracking
    domOps: z.number().default(0),
    domCount: z.number().default(0),

    // SEO meta tags
    seoTitle: optionalNonNull(z.string()),
    seoDesc: optionalNonNull(z.string()),

    // Open Graph
    ogTitle: optionalNonNull(z.string()),
    ogTitleCopied: optionalNonNull(z.boolean()),
    ogDesc: optionalNonNull(z.string()),
    ogDescCopied: optionalNonNull(z.boolean()),
    ogImages: optionalNonNull(z.array(z.string().nullable())),
    ogImageAssetId: objectIdString.nullable().default(null),

    // RSS Feed
    generateRss: z.boolean().nullish(),
    rssTitle: optionalNonNull(z.string()),
    rssDesc: optionalNonNull(z.string()),
    rssLink: z.string().nullish(),
    rssTtl: z.number().min(1).nullish(),
    rssItemTitle: optionalNonNull(z.string()),
    rssItemTitleCopied: optionalNonNull(z.boolean()),
    rssItemDesc: optionalNonNull(z.string()),
    rssItemDescCopied: optionalNonNull(z.boolean()),
    rssItemImage: z.string().nullish(),
    rssItemPubDateSlug: optionalNonNull(z.string()),

    // Search
    searchExclude: optionalNonNull(z.boolean()),
    searchTitle: optionalNonNull(z.string()),
    searchTitleCopied: z.boolean().default(true),
    searchDescription: optionalNonNull(z.string()),
    searchDescriptionCopied: z.boolean().default(true),
    searchImage: z.string().nullish(),
    searchImageCopied: z.boolean().default(true),
    searchSnippetOverride: optionalNonNull(z.string()),

    includeInSitemap: z.boolean().default(true),

    // JSON-LD Schema
    jsonLdSchema: optionalNonNull(z.string()),

    // Custom code
    head: optionalNonNull(z.string()),
    headOrder: optionalNonNull(CustomCodeOrder),
    postBody: optionalNonNull(z.string()),
    postBodyOrder: optionalNonNull(CustomCodeOrder),

    // CMS collection reference
    dynCollection: objectIdString.nullish(),

    // User systems (access control)
    groupAccess: z.array(objectIdString).default([]),

    // Publishing and lifecycle
    publishedOn: optionalNonNull(z.date()),
    archived: z.boolean().default(false),
    draft: z.boolean().default(false),
    // Admin-only flag set during DMCA or other legal holds. Prevents the page
    // from being compiled or published regardless of draft status. Cannot be
    // set by site owners — only clearable by admins.
    legalHold: z.boolean().default(false),
    createdOn: optionalNonNull(z.date()),
    createdBy: optionalNonNull(objectIdString),
    lastUpdated: z.date().default(() => new Date()),

    shouldPublish: z.boolean().default(true),

    // Branching
    canBranch: z.boolean().default(true),
    branchId: optionalNonNull(z.string()),
    standaloneBranch: z.boolean().default(false),

    // Site branching — optional so existing page documents parse unchanged
    /** Which of the branch's two trees this row belongs to. */
    branchRole: optionalNonNull(PageBranchRole),
    /**
     * The main page this row forked from. Unset means the page was created
     * inside the branch rather than forked from main.
     */
    branchMainPageId: optionalNonNull(objectIdString),
    /** Working rows only: points at this page's base-tree row. */
    branchBaseCopyId: optionalNonNull(objectIdString),
    /**
     * Set on a main page a site-branch merge created and hasn't made visible
     * yet: the `BranchJobJournal` that created it. A hidden page still holds
     * its slug, and reads that show pages to users or visitors leave it out.
     */
    mergeCreateJournalId: optionalNonNull(objectIdString),

    // Static Page Templates
    isStaticTemplate: z.boolean().default(false),
    duplicateOf: optionalNonNull(objectIdString),
    description: z.string().nullish(),

    // Localization
    /**
     * localizedSettings is a dynamic key object. It's shape is not precisely enough defined to be
     * represented with a Zod schema. Using a more strict type here could potentially
     * lead to runtime type errors. Using `z.unknown()` allows for skipping validation
     * at the cost of losing type safety when parsing. On the other hand, it forces type-checking
     * when writing code that deals with this object, so it's a good starting point
     * for future-proofing the schema, which should happen over time.
     */
    localizedSettings: optionalNonNull(z.unknown()),
    /**
     * Machine-translated page metadata: `[localeId][field] = {v, src}`, where
     * `src` fingerprints the primary `v` came from. Read only as a fallback
     * beneath `localizedSettings`. Kept out of PAGE_EDITABLE_FIELDS on
     * purpose; see the note there. Dynamic keys, hence `z.unknown()`.
     */
    autoTranslatedSettings: optionalNonNull(z.unknown()),
    canonicalUrl: z.string().nullable().default(null),

    // Page kind (e.g., component pages)
    pageKind: optionalNonNull(PageKind),
  })
  .describe('Represents a page in a Webflow site tree.');

export type Page = z.infer<typeof Page>;

export type StaticPageLimitCandidate = {
  type?: PageType;
  dynCollection?: unknown;
  utilKey?: string | null;
  isUtilityPage?: boolean;
  isStaticTemplate?: boolean;
  pageKind?: {kind?: string} | null;
  isComponentPage?: boolean;
  branchRole?: string | null;
};

/**
 * Whether a page's kind is eligible to count toward MAX_PAGES.
 * Lifecycle state (such as archived) is handled by the caller.
 */
export function isStaticPageLimitEligibleType(
  page: Readonly<StaticPageLimitCandidate>
): boolean {
  const hasUtilityPageKey =
    page.utilKey != null &&
    AVAILABLE_UTILITY_PAGE_KEYS.some((utilKey) => utilKey === page.utilKey);

  return (
    page.type !== 'folder' &&
    !page.dynCollection &&
    !page.isUtilityPage &&
    !hasUtilityPageKey &&
    !page.isStaticTemplate &&
    page.pageKind?.kind !== 'component' &&
    !page.isComponentPage &&
    !page.branchRole
  );
}

/**
 * A forked working-tree page row inserted during site-branch creation.
 *
 * Working rows are the editable tree. Each one must point at its frozen base
 * twin via `branchBaseCopyId`. `branchMainPageId` is required because this
 * contract is only for rows forked from a main page; pages created inside a
 * branch have no main-page lineage and do not go through this shape.
 */
export const WorkingSiteBranchPageRow = Page.extend({
  branchId: objectIdString,
  branchRole: z.literal(PageBranchRole.enum.working),
  branchMainPageId: objectIdString,
  branchBaseCopyId: objectIdString,
});
export type WorkingSiteBranchPageRow = z.infer<typeof WorkingSiteBranchPageRow>;

/**
 * A forked base-tree page row inserted during site-branch creation.
 *
 * Base rows are the frozen fork-time twin that diffs and merge run against.
 * They carry no `branchBaseCopyId` — that pointer is working-tree only, and an
 * id here is an invalid combination rather than a no-op. An explicit `null`
 * reads as "no pointer" and normalizes to `undefined`, matching how `Page`
 * treats the field, so a row read back out of Mongo still parses.
 * `branchMainPageId` is required because this contract is only for rows
 * forked from a main page; pages created inside a branch have no main-page
 * lineage and do not go through this shape.
 */
export const BaseSiteBranchPageRow = Page.extend({
  branchId: objectIdString,
  branchRole: z.literal(PageBranchRole.enum.base),
  branchMainPageId: objectIdString,
  branchBaseCopyId: optionalNonNull(z.undefined()),
});
export type BaseSiteBranchPageRow = z.infer<typeof BaseSiteBranchPageRow>;

/**
 * A forked site-branch page row: either the editable working twin or the
 * frozen base twin. Discriminated on `branchRole` so a working row without
 * `branchBaseCopyId`, or a base row with one, cannot be represented.
 *
 * Pages created inside a branch (no main-page lineage) are a different shape
 * and do not go through this contract.
 */
export const SiteBranchPageRow = z.discriminatedUnion('branchRole', [
  WorkingSiteBranchPageRow,
  BaseSiteBranchPageRow,
]);
export type SiteBranchPageRow = z.infer<typeof SiteBranchPageRow>;

/**
 * All fields that can be written through page.applyUpdate(), which filters
 * updates through this list.
 *
 * Do NOT add `autoTranslatedSettings`: the Designer PUTs the whole page record
 * on every save and a locale object in that payload replaces the persisted one
 * wholesale, so listing it here would let a stale client wipe translations the
 * autoTranslatePageMetadata job wrote (LOC-2482).
 */
export const PAGE_EDITABLE_FIELDS = [
  'title',
  'slug',
  'parent',
  'sortPos',
  'utilKey', // Not actually edited by UI once created
  'seoTitle',
  'seoDesc',
  'ogTitle',
  'ogTitleCopied',
  'ogDesc',
  'ogDescCopied',
  'ogImages',
  'ogImageAssetId',
  'generateRss',
  'localizedSettings',
  'rssTitle',
  'rssDesc',
  'rssLink',
  'rssTtl',
  'rssItemTitle',
  'rssItemTitleCopied',
  'rssItemDesc',
  'rssItemDescCopied',
  'rssItemImage',
  'rssItemPubDateSlug',
  'searchExclude',
  'searchTitle',
  'searchTitleCopied',
  'searchDescription',
  'searchDescriptionCopied',
  'searchImage',
  'searchImageCopied',
  'searchSnippetOverride',
  'groupAccess',
  'includeInSitemap',
  'head',
  'postBody',
  'draft',
  'canBranch',
  'standaloneBranch',
  'shouldPublish',
  'isStaticTemplate',
  'description',
  'canonicalUrl',
  'pageKind',
  'jsonLdSchema',
];

/**
 * Fields that require sitePage.modify — excluded from the manage tier.
 * Must stay in sync with the FE canModifyPage gating in PageSettings.tsx.
 */
export const MODIFY_ONLY_FIELDS: ReadonlySet<string> = new Set([
  'title',
  'slug',
  'parent',
]);

/**
 * Subset of PAGE_EDITABLE_FIELDS allowed for users with only
 * sitePageSettings.manage (no sitePage.modify).
 */
export const PAGE_FIELDS_MANAGE_TIER: ReadonlySet<string> = new Set(
  PAGE_EDITABLE_FIELDS.filter((field) => !MODIFY_ONLY_FIELDS.has(field))
);
